import React from 'react';
import { View, Text, StyleSheet, Platform, TouchableOpacity } from 'react-native';
import Svg, { Path, Rect } from 'react-native-svg';
import FontFamily from '../../../constants/fonts';

interface TopOpportunitiesCardProps {
  title: string;
  description: string;
  taskType: string;
  date: string;
  onAddPress?: () => void;
  onCardPress?: () => void;
  width?: number | string;
  height?: number | string;
  status?: string;
  statusColor?: string;
}

const DragHandleIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path
      d="M7.33341 12.0003C7.33341 12.7337 6.73341 13.3337 6.00008 13.3337C5.26675 13.3337 4.66675 12.7337 4.66675 12.0003C4.66675 11.267 5.26675 10.667 6.00008 10.667C6.73341 10.667 7.33341 11.267 7.33341 12.0003ZM6.00008 6.66699C5.26675 6.66699 4.66675 7.26699 4.66675 8.00033C4.66675 8.73366 5.26675 9.33366 6.00008 9.33366C6.73341 9.33366 7.33341 8.73366 7.33341 8.00033C7.33341 7.26699 6.73341 6.66699 6.00008 6.66699ZM6.00008 2.66699C5.26675 2.66699 4.66675 3.26699 4.66675 4.00033C4.66675 4.73366 5.26675 5.33366 6.00008 5.33366C6.73341 5.33366 7.33341 4.73366 7.33341 4.00033C7.33341 3.26699 6.73341 2.66699 6.00008 2.66699ZM10.0001 5.33366C10.7334 5.33366 11.3334 4.73366 11.3334 4.00033C11.3334 3.26699 10.7334 2.66699 10.0001 2.66699C9.26675 2.66699 8.66675 3.26699 8.66675 4.00033C8.66675 4.73366 9.26675 5.33366 10.0001 5.33366ZM10.0001 6.66699C9.26675 6.66699 8.66675 7.26699 8.66675 8.00033C8.66675 8.73366 9.26675 9.33366 10.0001 9.33366C10.7334 9.33366 11.3334 8.73366 11.3334 8.00033C11.3334 7.26699 10.7334 6.66699 10.0001 6.66699ZM10.0001 10.667C9.26675 10.667 8.66675 11.267 8.66675 12.0003C8.66675 12.7337 9.26675 13.3337 10.0001 13.3337C10.7334 13.3337 11.3334 12.7337 11.3334 12.0003C11.3334 11.267 10.7334 10.667 10.0001 10.667Z"
      fill="#034895"
    />
  </Svg>
);

const AddIcon = () => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path
      d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z"
      fill="#2196F3"
    />
  </Svg>
);

const CalendarIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Rect x="2" y="3" width="12" height="11" rx="2" stroke="#616161" strokeWidth="1.5" fill="none" />
    <Path d="M2 6H14" stroke="#616161" strokeWidth="1.5" />
    <Path d="M5 2V4" stroke="#616161" strokeWidth="1.5" strokeLinecap="round" />
    <Path d="M11 2V4" stroke="#616161" strokeWidth="1.5" strokeLinecap="round" />
  </Svg>
);

const TopOpportunitiesCard: React.FC<TopOpportunitiesCardProps> = ({
  title,
  description,
  taskType,
  date,
  onAddPress,
  onCardPress,
  width,
  height,
  status,
  statusColor,
}) => {
  const cardStyle: any = [styles.card];
  if (width !== undefined) cardStyle.push({ width });
  if (height !== undefined) cardStyle.push({ height });

  return (
    <TouchableOpacity 
      style={cardStyle} 
      onPress={onCardPress}
      activeOpacity={0.7}
    >
      {/* Left: Drag Handle */}
      {/* <View style={styles.dragHandle}>
        <DragHandleIcon />
      </View> */}

      {/* Center: Content */}
      <View style={styles.content}>
        {/* Title Row with Drag Handle and Add Button */}
        <View style={styles.titleRow}>
            <View style={styles.leftTitleRow}>
              {/* <View style={styles.dragHandle}>
                <DragHandleIcon />
              </View> */}
              <Text style={styles.title} numberOfLines={1} ellipsizeMode="tail">
                {title}
              </Text>
              {status && status ==='overdue' &&(
                <View style={[styles.inlineStatus, { backgroundColor: statusColor || '#C12C01' }]}>
                  <Text style={styles.inlineStatusText}>{status}</Text>
                </View>
              )}
              {/** Inline status badge */}
              {typeof (TopOpportunitiesCard as any).status !== 'undefined' ? null : null}
              {/** We'll use the passed props `status` and `statusColor` */}
              {/** Render when provided */}
              {/** Placeholder; actual render below after styles added */}
            </View>

          <TouchableOpacity
            style={styles.addButton}
            onPress={(e) => {
              e.stopPropagation && e.stopPropagation();
              onAddPress && onAddPress();
            }}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <AddIcon />
          </TouchableOpacity>
        </View>

        <Text style={styles.description}>{description}</Text>
        <View style={styles.footer}>
          <Text style={styles.taskType}>{taskType}</Text>
          <View style={styles.dateContainer}>
            <CalendarIcon />
            <Text style={styles.date}>{date}</Text>
          </View>
        </View>
      </View>

      {/* Right add button moved into title row */}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    minHeight: 70,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  dragHandle: {
    width: 16,
    height: 16,
    marginLeft: -2,
  },
  content: {
    flex: 1,
    gap: 4,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  leftTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginRight: 8,
  },
  title: {
    flexShrink: 1,
    fontSize: 14,
    fontWeight: '500',
    color: '#1C1C1C',
    fontFamily: FontFamily.medium,
    marginLeft: 3,
  },
  description: {
    fontSize: 10,
    fontWeight: '400',
    color: '#4B4B4B',
    fontFamily: FontFamily.regular,
    marginBottom: 6,
    lineHeight: 14,
    paddingHorizontal:4,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal:4
  },
  taskType: {
    fontSize: 10,
    fontWeight: '400',
    color: '#4B4B4B',
    fontFamily: FontFamily.regular,
  },
  dateContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  date: {
    fontSize: 10,
    fontWeight: '400',
    color: '#4B4B4B',
    fontFamily: FontFamily.regular,
  },
  addButton: {
    justifyContent: 'center',
    alignItems: 'center',
    width: 20,
    height: 20,
  },
  inlineStatus: {
    marginLeft: 8,
    paddingHorizontal: 6,
    paddingVertical: 4,
    borderRadius: 99,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  inlineStatusText: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
});

export default TopOpportunitiesCard;
